<template>
  <div
    class="kpi-rings-container"
    @mouseleave="hoveredRingIndex = -1"
  >
    <svg
      class="kpi-ring"
      viewBox="0 0 40 40"
    >
      <defs>
        <linearGradient
          v-for="(ring, index) in ringProperties"
          :id="`gradient-${index}`"
          :key="index"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="0%"
        >
          <stop
            offset="0%"
            :stop-color="ring.colorStart"
          />
          <stop
            offset="100%"
            :stop-color="ring.colorEnd"
          />
        </linearGradient>
        <filter
          id="ringShadow"
          x="-20%"
          y="-20%"
          width="140%"
          height="140%"
        >
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="0.5"
            flood-color="black"
            flood-opacity="0.2"
          />
        </filter>
      </defs>
      <path
        v-for="(ring, index) in ringProperties"
        :key="`bg-${index}`"
        :class="`kpi-ring-background kpi-ring-background-${index}`"
        :d="ringD(index)"
        transform="rotate(90 20 20)"
        :style="{ stroke: ring.background, strokeWidth: ring.strokeWidth }"
        @mouseover="hoveredRingIndex = index"
      />
      <path
        v-for="(ring, index) in ringProperties"
        :key="`value-${index}`"
        :class="`kpi-ring-value kpi-ring-value-${index}`"
        :d="ringD(index)"
        transform="rotate(90 20 20)"
        :style="[
          ringStyle(modelValue[index]?.value || 0, index),
          hoveredRingIndex === index && ringConfigs?.value?.strokeWidth
            ? { strokeWidth: `${ringConfigs.value.strokeWidth + 0.5}px` }
            : {}
        ]"
        :filter="hoveredRingIndex === index ? 'url(#ringShadow)' : ''"
        @mouseover="hoveredRingIndex = index"
        @mouseleave="hoveredRingIndex = -1"
      />
      <text
        v-if="hoverStats && hoveredRingIndex !== -1 && ringProperties[hoveredRingIndex]"
        x="50%"
        y="45%"
        text-anchor="middle"
        alignment-baseline="central"
        :style="{ fill: 'grey', fontSize: '2px' }"
      >
        {{ ringProperties[hoveredRingIndex].ring }}
      </text>
      <text
        v-if="hoverStats && hoveredRingIndex !== -1 && ringProperties[hoveredRingIndex] && modelValue[hoveredRingIndex]"
        x="50%"
        y="55%"
        text-anchor="middle"
        alignment-baseline="central"
        :style="{
          fill: ringProperties[hoveredRingIndex].colorStart,
          fontSize: '4px',
          fontWeight: 'bold'
        }"
      >
        {{ numberFormatter(modelValue[hoveredRingIndex]?.value || 0, 'number', 0) }}%
      </text>
    </svg>
  </div>
</template>

<script setup>
// composables
import { ref, computed } from 'vue'

/**
 * Component props for KpiRings
 */
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [{ value: 90 }, { value: 80 }, { value: 70 }, { value: 60 }, { value: 50 }, { value: 40 }]
  },
  size: {
    type: String,
    default: 'md', // Changed default to 'md' to match the computed logic
    validator: value => ['sm', 'md', 'lg'].includes(value)
  },
  hoverStats: {
    type: Boolean,
    default: false
  }
})

/**
 * Reactive reference for tracking which ring is currently hovered
 */
const hoveredRingIndex = ref(-1) // -1 indicates no ring is hovered

/**
 * Configuration for each ring including colors, icons, and stroke properties
 */
const ringProperties = ref([
  {
    ring: 'Productivity',
    icon: 'test6',
    colorStart: '#00D2FF',
    colorEnd: '#34D1BF',
    backgroundOpacity: '15',
    strokeWidth: 1.5
  },
  {
    ring: 'Answer',
    icon: 'test',
    colorStart: '#ff595e',
    colorEnd: '#FF5993',
    backgroundOpacity: '15',
    strokeWidth: 1.5
  },
  {
    ring: 'Opportunity',
    icon: 'test4',
    colorStart: '#00A6ED',
    colorEnd: '#007BFF',
    backgroundOpacity: '15',
    strokeWidth: 1.5
  },
  {
    ring: 'Conversion',
    icon: 'test2',
    colorStart: '#F7B32B',
    colorEnd: '#ffca3a',
    backgroundOpacity: '15',
    strokeWidth: 1.5
  },
  {
    ring: 'App v Call',
    icon: 'test3',
    colorStart: '#8ac926',
    colorEnd: '#B4C926',
    backgroundOpacity: '15',
    strokeWidth: 1.5
  }
  // { ring: 'Compliance', icon: 'test5', colorStart: '#9D00FF', colorEnd: '#E500FF', backgroundOpacity: '15', strokeWidth: 1.5 }
])

/**
 * Computed configuration for ring sizes and stroke widths based on the size prop
 * @returns {Object} Configuration object with baseRadii and strokeWidth
 */
const ringConfigs = computed(() => {
  try {
    let baseRadii = [19, 17, 15, 13, 11, 9] // Base values for 'md'
    let strokeWidth = 1.5 // Base stroke width for 'md'

    switch (props.size) {
      case 'sm':
        baseRadii = [18, 15, 12, 9, 6, 3]
        strokeWidth *= 2
        break
      case 'lg':
        baseRadii = [18, 16, 14, 12, 10, 8]
        strokeWidth *= 1.2
        break
      default:
        // 'md' case uses default values
        break
    }

    return { baseRadii, strokeWidth }
  } catch (error) {
    console.error('Error computing ring configs:', error)
    // Return safe fallback values
    return { baseRadii: [19, 17, 15, 13, 11, 9], strokeWidth: 1.5 }
  }
})

/**
 * Initialize ring properties with background colors
 */
try {
  ringProperties.value.forEach(ring => {
    if (ring && ring.colorStart) {
      const opacityHex = Math.floor(255 * 0.1)
        .toString(16)
        .padStart(2, '0')
      ring.background = `${ring.colorStart}${opacityHex}`
    }
  })
} catch (error) {
  console.error('Error initializing ring properties:', error)
}

/**
 * Generates the SVG path data for a ring at the specified index
 * @param {number} index - The index of the ring
 * @returns {string} SVG path data string
 */
const ringD = index => {
  try {
    if (!ringConfigs.value?.baseRadii || !Array.isArray(ringConfigs.value.baseRadii)) {
      console.warn('ringConfigs.baseRadii is not available, using fallback')
      return 'M20 20 m -19, 0 a 19,19 0 1 1 38,0 a 19,19 0 1 1 -38,0'
    }

    const radius = ringConfigs.value.baseRadii[index % ringConfigs.value.baseRadii.length]
    if (typeof radius !== 'number' || isNaN(radius)) {
      console.warn(`Invalid radius at index ${index}, using fallback`)
      return 'M20 20 m -19, 0 a 19,19 0 1 1 38,0 a 19,19 0 1 1 -38,0'
    }

    return `M20 20 m -${radius}, 0 a ${radius},${radius} 0 1 1 ${radius * 2},0 a ${radius},${radius} 0 1 1 -${radius * 2},0`
  } catch (error) {
    console.error('Error generating ring path:', error)
    // Return safe fallback path
    return 'M20 20 m -19, 0 a 19,19 0 1 1 38,0 a 19,19 0 1 1 -38,0'
  }
}

/**
 * Generates the style object for a ring based on its value and index
 * @param {number} value - The percentage value of the ring (0-100)
 * @param {number} index - The index of the ring
 * @returns {Object} Style object with stroke properties
 */
const ringStyle = (value, index) => {
  try {
    if (!ringConfigs.value?.baseRadii || !ringConfigs.value?.strokeWidth) {
      console.warn('ringConfigs not available, using fallback values')
      return {
        strokeDasharray: '119.38 119.38',
        strokeDashoffset: 0,
        stroke: `url(#gradient-${index})`,
        strokeWidth: 1.5,
        animation: 'none'
      }
    }

    const radius = ringConfigs.value.baseRadii[index % ringConfigs.value.baseRadii.length]
    if (typeof radius !== 'number' || isNaN(radius)) {
      console.warn(`Invalid radius at index ${index}, using fallback`)
      return {
        strokeDasharray: '119.38 119.38',
        strokeDashoffset: 0,
        stroke: `url(#gradient-${index})`,
        strokeWidth: ringConfigs.value.strokeWidth || 1.5,
        animation: 'none'
      }
    }

    const circumference = 2 * Math.PI * radius
    const safeValue = typeof value === 'number' && !isNaN(value) ? Math.max(0, Math.min(100, value)) : 0
    const visibleLength = (safeValue / 100) * circumference

    return {
      strokeDasharray: `${visibleLength} ${circumference}`,
      strokeDashoffset: 0,
      stroke: `url(#gradient-${index})`,
      strokeWidth: ringConfigs.value.strokeWidth || 1.5,
      animation: 'none'
    }
  } catch (error) {
    console.error('Error generating ring style:', error)
    // Return safe fallback style
    return {
      strokeDasharray: '119.38 119.38',
      strokeDashoffset: 0,
      stroke: `url(#gradient-${index})`,
      strokeWidth: 1.5,
      animation: 'none'
    }
  }
}

/**
 * Formats numbers for display
 * @param {number} value - The value to format
 * @param {string} type - The type of formatting
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
const numberFormatter = (value, type, decimals) => {
  try {
    if (typeof value !== 'number' || isNaN(value)) {
      return '0'
    }

    if (type === 'number') {
      return value.toFixed(decimals || 0)
    }

    return value.toString()
  } catch (error) {
    console.error('Error formatting number:', error)
    return '0'
  }
}
</script>

<style scoped>
.kpi-rings-container {
  display: flex;
  justify-content: center;
}

.kpi-ring text {
  font-family: 'Arial', sans-serif;
  font-size: 3px;
  /* Adjust based on your SVG's viewBox scale */
  pointer-events: none;
  /* Prevents text from interfering with hover events */
}

.kpi-ring-background,
.kpi-ring-value {
  fill: none;
  stroke-linecap: round;
  /* Makes the stroke ends nicely rounded */
}
</style>

<!-- Example Usage -->
<!-- <template>
  <div class="flex justify-between items-center p-20">
    <div class="w-12 h-12">
      <KpiRings v-model="data" size="sm" />
    </div>
    <div class="w-44 h-44">
      <KpiRings v-model="data" size="md" :hover-stats="true" />
    </div>
    <div class="w-64 h-64">
      <KpiRings v-model="data" size="lg" :hover-stats="true" />
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import KpiRings from '@/components/dashboard/KpiRings.vue'
export default {
  components: { KpiRings },
  setup() {
    const data = ref([{ value: 90 }, { value: 80 }, { value: 70 }, { value: 60 }, { value: 50 }, { value: 40 }])
    return { data }
  }
}
</script>

<style></style> -->
